import AppStoreBadge from '@/components/AppStoreBadge';
import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingUp, BarChart3, MessageSquare, Shield, FileText, Scale, Building2 } from 'lucide-react';

const SEOFooter: React.FC = () => {
  return (
    <footer className="w-full border-t border-border bg-muted/30 mt-16">
      {/* JSON-LD Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'EquiForge',
            alternateName: 'Streamwalkers Corporation',
            url: 'https://equiforge.ai',
            logo: 'https://equiforge.ai/lovable-uploads/90a7612d-e82b-47db-b1df-3cea1d8d2979.png',
            description: 'AI-powered mortgage calculator, real estate analytics platform, and homebuyer intelligence tools.',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'info@equiforge.com',
              contactType: 'customer service',
            },
            sameAs: [],
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 flex flex-col items-start gap-3">
          <p className="text-sm font-medium">EquiForge for iOS</p>
          <AppStoreBadge />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {/* Product Tools */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Mortgage Tools</h3>
            <ul className="space-y-2.5">
              <li><Link to="/try/payment" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><Calculator className="w-3.5 h-3.5" />Mortgage Payment Calculator</Link></li>
              <li><Link to="/try/reverse-piti" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><TrendingUp className="w-3.5 h-3.5" />Reverse PITI Calculator</Link></li>
              <li><Link to="/try" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><Calculator className="w-3.5 h-3.5" />Free Mortgage Calculators</Link></li>
              <li><Link to="/signup" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><BarChart3 className="w-3.5 h-3.5" />SmartBuy Deal Analyzer</Link></li>
              <li><Link to="/signup" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><MessageSquare className="w-3.5 h-3.5" />CLARA AI Homebuyer Assistant</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Resources</h3>
            <ul className="space-y-2.5">
              <li><Link to="/home" className="text-sm hover:text-primary transition-colors">Home Affordability Guide</Link></li>
              <li><Link to="/about" className="text-sm hover:text-primary transition-colors">About EquiForge</Link></li>
              <li><Link to="/brochure" className="text-sm hover:text-primary transition-colors">Platform Overview & Brochure</Link></li>
              <li><Link to="/signup" className="text-sm hover:text-primary transition-colors">Create Free Account</Link></li>
              <li><Link to="/login" className="text-sm hover:text-primary transition-colors">Sign In to Dashboard</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Legal & Privacy</h3>
            <ul className="space-y-2.5">
              <li><Link to="/privacy" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><Shield className="w-3.5 h-3.5" />Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><FileText className="w-3.5 h-3.5" />Terms of Service</Link></li>
              <li><Link to="/eula" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><Scale className="w-3.5 h-3.5" />End User License Agreement</Link></li>
              <li><Link to="/cookie-policy" className="text-sm hover:text-primary transition-colors">Cookie Policy</Link></li>
              <li><Link to="/disclaimers" className="text-sm hover:text-primary transition-colors">Financial Disclaimers</Link></li>
            </ul>
          </div>

          {/* Company & Compliance */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Security & Compliance</h3>
            <ul className="space-y-2.5">
              <li><Link to="/compliance" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><Shield className="w-3.5 h-3.5" />SOC 2 Type II Certified</Link></li>
              <li><Link to="/compliance" className="text-sm hover:text-primary transition-colors">ISO 27001 Compliance</Link></li>
              <li><Link to="/compliance" className="text-sm hover:text-primary transition-colors">GDPR & CCPA Compliance</Link></li>
              <li><Link to="/legal-hub" className="flex items-center gap-2 text-sm hover:text-primary transition-colors"><Building2 className="w-3.5 h-3.5" />Legal & Privacy Center</Link></li>
              <li><a href="mailto:security@equiforge.com" className="text-sm hover:text-primary transition-colors">Report a Vulnerability</a></li>
            </ul>
          </div>
        </div>

        {/* Keyword-rich description */}
        <div className="border-t border-border pt-8 space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed max-w-5xl">
            <strong>EquiForge™</strong> is the leading <strong>AI-powered mortgage calculator</strong> and <strong>real estate analytics platform</strong>. 
            Calculate your <Link to="/try/payment" className="text-primary hover:underline">monthly mortgage payment</Link> including principal, interest, taxes, and insurance (PITI). 
            Use our <Link to="/try/reverse-piti" className="text-primary hover:underline">reverse PITI calculator</Link> to find out <strong>how much house you can afford</strong> based on your desired monthly payment. 
            EquiForge provides <strong>home loan calculators</strong>, <strong>real estate investment analysis</strong>, <strong>property comparison tools</strong>, 
            and an <strong>AI homebuyer assistant</strong> to help first-time homebuyers and experienced investors make confident decisions. 
            Our platform features <strong>mortgage payment estimators</strong>, <strong>closing cost calculators</strong>, <strong>debt-to-income ratio tools</strong>, 
            and <strong>home affordability calculators</strong> — all powered by real-time market data and advanced AI analytics.
          </p>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>© {new Date().getFullYear()} Streamwalkers™ Corporation. All rights reserved. EquiForge™ is a registered trademark.</p>
            <div className="flex items-center gap-4">
              <span>info@equiforge.com</span>
              <span>•</span>
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacy</Link>
              <span>•</span>
              <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms</Link>
              <span>•</span>
              <Link to="/compliance" className="hover:text-primary transition-colors">Security</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SEOFooter;
